import { useState } from "react";
import { cn } from "@/lib/utils";

interface VirtualKeyboardProps {
  onKeyClick: (key: string) => void;
  direction: "latinToCyrillic" | "cyrillicToLatin" | "russianToKazakh" | "englishToKazakhLatin";
}

export function VirtualKeyboard({ onKeyClick, direction }: VirtualKeyboardProps) {
  const [capsLock, setCapsLock] = useState(false);

  const toggleCapsLock = () => {
    setCapsLock(!capsLock);
  };

  const applyCase = (key: string) => {
    if (!capsLock) return key;

    // Handle single characters
    if (key.length === 1) {
      return key.toUpperCase();
    }

    // Handle multi-character special keys (e.g., "a'", "ch", "sh", etc.)
    // For apostrophe notation (a', o', u', etc.), uppercase the letter before apostrophe
    if (key.includes("'")) {
      return key.toUpperCase();
    }

    // For digraphs (ch, sh, zh, ng, gh), uppercase all letters
    return key.toUpperCase();
  };
  // Define keyboard layouts based on direction
  const getKeyboardLayout = () => {
    switch (direction) {
      case "latinToCyrillic":
        return {
          rows: [
            ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
            ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
            ["z", "x", "c", "v", "b", "n", "m"],
          ],
          specialKeys: ["a'", "o'", "u'", "i'", "y'", "g'", "ch", "sh", "zh", "ng"],
        };

      case "cyrillicToLatin":
        return {
          rows: [
            ["а", "б", "в", "г", "д", "е", "ж", "з", "и", "й"],
            ["к", "л", "м", "н", "о", "п", "р", "с", "т", "у"],
            ["ф", "х", "ц", "ч", "ш", "ы", "э"],
          ],
          specialKeys: ["ә", "ө", "ү", "ұ", "қ", "ғ", "һ", "і", "ң"],
        };

      case "russianToKazakh":
        return {
          rows: [
            ["а", "б", "в", "г", "д", "е", "ё", "ж", "з", "и"],
            ["й", "к", "л", "м", "н", "о", "п", "р", "с", "т"],
            ["у", "ф", "х", "ц", "ч", "ш", "щ", "ы", "э"],
          ],
          specialKeys: ["ъ", "ь", "ю", "я"],
        };

      case "englishToKazakhLatin":
        return {
          rows: [
            ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j"],
            ["k", "l", "m", "n", "o", "p", "q", "r", "s", "t"],
            ["u", "v", "w", "x", "y", "z"],
          ],
          specialKeys: ["ä", "ö", "ü", "ū", "ğ", "ş", "ñ", "ı", "ç"],
        };

      default:
        return { rows: [], specialKeys: [] };
    }
  };

  const layout = getKeyboardLayout();

  return (
    <div className="w-full bg-card border border-input rounded-xl p-3 sm:p-4 mt-2 sm:mt-3">
      <div className="flex flex-col gap-2">
        {/* Main keyboard rows */}
        {layout.rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-1 sm:gap-2">
            {row.map((key) => (
              <button
                key={key}
                onClick={() => onKeyClick(applyCase(key))}
                className={cn(
                  "flex items-center justify-center min-w-[32px] sm:min-w-[40px] h-9 sm:h-10",
                  "px-2 sm:px-3 rounded-md",
                  "bg-secondary hover:bg-secondary/80",
                  "text-foreground font-medium text-sm sm:text-base",
                  "transition-colors touch-manipulation",
                  "active:scale-95 transform"
                )}
              >
                {applyCase(key)}
              </button>
            ))}
          </div>
        ))}

        {/* Special characters row */}
        {layout.specialKeys.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <div className="text-xs sm:text-sm text-muted-foreground mb-2 text-center">
              Special Characters
            </div>
            <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
              {layout.specialKeys.map((key) => (
                <button
                  key={key}
                  onClick={() => onKeyClick(applyCase(key))}
                  className={cn(
                    "flex items-center justify-center min-w-[40px] sm:min-w-[48px] h-9 sm:h-10",
                    "px-2 sm:px-3 rounded-md",
                    "bg-accent hover:bg-accent/80",
                    "text-accent-foreground font-medium text-sm sm:text-base",
                    "transition-colors touch-manipulation",
                    "active:scale-95 transform"
                  )}
                >
                  {applyCase(key)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Utility keys */}
        <div className="flex justify-center gap-2 mt-2">
          <button
            onClick={toggleCapsLock}
            className={cn(
              "flex items-center justify-center px-3 sm:px-4 h-9 sm:h-10",
              "rounded-md",
              "text-foreground text-sm sm:text-base font-medium",
              "transition-colors touch-manipulation",
              "active:scale-95 transform",
              capsLock
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-secondary hover:bg-secondary/80"
            )}
            title="Toggle Caps Lock"
          >
            ⇪ Caps
          </button>
          <button
            onClick={() => onKeyClick(" ")}
            className={cn(
              "flex items-center justify-center px-8 sm:px-12 h-9 sm:h-10",
              "rounded-md",
              "bg-secondary hover:bg-secondary/80",
              "text-foreground text-sm sm:text-base",
              "transition-colors touch-manipulation",
              "active:scale-95 transform"
            )}
          >
            Space
          </button>
          <button
            onClick={() => onKeyClick("\n")}
            className={cn(
              "flex items-center justify-center px-4 sm:px-6 h-9 sm:h-10",
              "rounded-md",
              "bg-secondary hover:bg-secondary/80",
              "text-foreground text-sm sm:text-base",
              "transition-colors touch-manipulation",
              "active:scale-95 transform"
            )}
          >
            Enter
          </button>
          <button
            onClick={() => onKeyClick("⌫")}
            className={cn(
              "flex items-center justify-center px-4 sm:px-6 h-9 sm:h-10",
              "rounded-md",
              "bg-destructive/20 hover:bg-destructive/30",
              "text-foreground text-sm sm:text-base",
              "transition-colors touch-manipulation",
              "active:scale-95 transform"
            )}
          >
            ⌫
          </button>
        </div>
      </div>
    </div>
  );
}
